/**
 * QUIL — Sync engine
 * Upload: drains the sync queue to OneDrive with exponential backoff.
 * Download: delta query → apply changes to Dexie (LWW by timestamp).
 * Conflict resolution for prose is flagged here; dialogue is Phase 5 UI.
 */

import type { Table } from 'dexie'
import { db, withoutRetired } from './db'
import type { Work, Chapter, SyncQueueEntry, AggregateEntityType, Tombstone, SyncStatus } from './db'
import { ensureQuilFolders, ensureWorkFolders, writeFileToOneDrive, deleteFileFromOneDrive, readFileFromOneDrive, runDeltaQuery, isDocConflict } from './onedrive'
import { GraphNetworkError, type DocConflictError } from './graphErrors'
import type { DeltaItem, RemoteFile, WrittenFile } from './onedrive'
import { appSyncLock } from './syncCoordination'
import { writeRebasingItem, type WritePrecondition } from './writePrecondition'
import { QUIL_ONEDRIVE_ROOT } from './auth'
import { ensureSettings } from './db'
import { QUEUE_UPDATED_EVENT, reconcileLocalToCloud, sweepCrashFlushedProse, reEnqueueWorkMeta, reEnqueueChapterMeta, TOMBSTONE_RETENTION_MS } from './storage'
import { logSync, setSyncActive } from './syncMonitor'
import { serverNow } from './clock'
import { configureErrorSync, errorSyncConfig, flushErrorLog } from './errorLog'
import { noteSyncSuccess } from './expectations'
import { isInteractiveSignInRequired } from './authToken'
import { APP_ID as ERROR_SYNC_APP, runQuilChecks } from './healthChecks'

/** Error sync: a load, pull or upload drain just succeeded, so the OneDrive path is
 *  known-good. Record it for the sync-silent check and push any buffered errors to this
 *  device's errors file (lazy, best-effort, never throws). */
function noteErrorSyncSuccess(): void {
  noteSyncSuccess(ERROR_SYNC_APP)
  void flushErrorLog().catch(() => undefined)
  // W12 Phase B housekeeping that follows a good pass: migration-backup retention and the weekly library
  // snapshot, once per session (lazy: recoveryBoot.ts stays out of the entry chunk).
  void import('./recoveryBoot').then(m => m.afterSuccessfulSync()).catch(() => undefined)
}

/** Per-item progress callback: (settled-so-far, total). Fired by the down/up loops. */
type ProgressItemFn = (done: number, total: number) => void

/**
 * Genuine cold-start milestones, surfaced to the loading gate. Every field is a
 * real observable — never a fabricated total. `phase` is the lifecycle stage;
 * `itemsDone`/`itemsTotal` are the live counts from whichever loop is running
 * (delta items pulled, or queue entries pushed); `stories` is the real
 * `db.works` count once it's known (from `merging` onward). See runInitialSync.
 */
export interface InitialSyncProgress {
  phase: 'connecting' | 'pulling' | 'merging' | 'saving' | 'done'
  itemsDone?: number
  itemsTotal?: number
  stories?: number
}

type InitialSyncProgressFn = (p: InitialSyncProgress) => void

// 10 tries with the delay capped at 15 min gives a ~30-minute ladder that rides
// out a real Graph outage without parking (audit 3.6); parking stays for writes
// OneDrive genuinely rejects, where retrying can't help.
const MAX_ENTRY_RETRIES = 10
const MAX_RETRY_DELAY_S = 900
const AUTH_RETRY_DELAY_MS = 5 * 60_000
const OFFLINE_RETRY_DELAY_MS = 60_000

/**
 * True for "the user must interact to get a token" failures (expired refresh token,
 * a background popup that got blocked). These must NOT burn retries toward the
 * parked 'error' state — sync should just pause and resume once the user re-auths (B4).
 */
// Exported for unit tests (T5) — the regex must keep matching the real MSAL error
// strings; a silent drift would let an auth failure burn retries and park the queue.
/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export function isAuthInteractionError(err: unknown): boolean {
  // The canonical token getter reports this type for every "the user must act" case, including
  // the signed-out one the old 'no_account' string carried. Checked BY TYPE and first, because
  // the regex below would not match it: `InteractiveSignInRequiredError` does not contain
  // `InteractionRequired`, and missing it here would burn an entry's retries and park the queue
  // on the offline trusted-device path (audit 2.2) — the exact failure this function exists to
  // prevent. W9.
  if (isInteractiveSignInRequired(err)) return true
  const s = String((err as { errorCode?: string; name?: string; message?: string })?.errorCode
    ?? (err as Error)?.name ?? (err as Error)?.message ?? err)
  return /interaction_required|popup|user_cancelled|InteractionRequired|no_account|login_required|consent_required/i.test(s)
}

/**
 * True for network-level failures (offline, DNS, connection reset). The shared Graph
 * client retries a rejected fetch and then throws GraphNetworkError (W10); a raw
 * TypeError is still accepted for anything outside it. The explicit offline signal
 * is trusted too.
 * Like auth failures (B4), these are not the entry's fault — the drain pauses
 * WITHOUT burning retries, so an offline writing session can never park the queue
 * into the manual-retry-only 'error' state (audit 2.1).
 */
// Exported for unit tests (T5) alongside isAuthInteractionError.
/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  return err instanceof GraphNetworkError || err instanceof TypeError
}

/**
 * Make parked entries (nextRetryAt = MAX_SAFE_INTEGER after exhausted retries)
 * due again. Called on reconnect so a queue parked during an outage — or by an
 * older build without the offline classification — always resumes by itself.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export async function unparkSyncQueue(): Promise<void> {
  await db.syncQueue
    .where('nextRetryAt').equals(Number.MAX_SAFE_INTEGER)
    .modify({ nextRetryAt: 0, retryCount: 0 })
}

// ─── The conflict paths, loaded on demand ─────────────────────────────────────
// Refusals, recreated writes, deletes and remote deletions go through the shared W11
// decision modules in lib/syncConflicts.ts. None of that runs on an ordinary save, so it
// is a dynamic import and stays out of the entry chunk (the 600 KiB budget).
const conflictPaths = () => import('./syncConflicts')

// ─── Prose upload with conflict detection ──────────────────────────────────────

/** A prose (.prose.json) upload: an authored whole, refused into a chooser (W11 'choose'). */
function isProseEntry(entry: SyncQueueEntry): boolean {
  return entry.entityType === 'chapter' && entry.remotePath.endsWith('.prose.json')
}

/** A chapter's own meta file ({id}.json): LWW by content stamp, now conditional (ledger #15). */
function isChapterMetaEntry(entry: SyncQueueEntry): boolean {
  return entry.entityType === 'chapter' && entry.entityId.endsWith(':meta')
}

/**
 * Upload a chapter's prose with If-Match = the stored cTag (the OneDrive eTag),
 * so a concurrent edit on another device is rejected with 412 rather than
 * silently overwritten (NFR-20). On success the chapter's cTag is advanced so
 * the NEXT edit's If-Match is current. On a refusal both versions are kept as a
 * ConflictRecord and the error re-thrown so the caller drops the entry (unless the
 * remote vanished meanwhile: then it stays queued, and its next attempt recreates).
 * A recreated write (the file had gone) goes through check-moved, then edit wins.
 * A chapter with no cTag has never synced (new id): a plain create, no If-Match.
 */
async function uploadProse(entry: SyncQueueEntry): Promise<void> {
  const chapter = await db.chapters.get(entry.entityId)
  let written: WrittenFile
  try {
    written = await writeFileToOneDrive(entry.remotePath, entry.payload!, chapter?.cTag)
  } catch (err) {
    if (isDocConflict(err)) {
      const recorded = await (await conflictPaths()).recordProseConflict(entry, (err as DocConflictError).status ?? 412)
      if (!recorded) throw new Error('prose refused, then gone: retrying as a recreate', { cause: err })
    }
    throw err
  }
  await db.chapters.update(entry.entityId, { cTag: written.etag, ...(written.cTag ? { proseCTag: written.cTag } : {}) })
  if (written.recreated) await (await conflictPaths()).onProseRecreated(entry, written)
}

// ─── Meta and aggregate uploads, conditional (W11, ledger #15) ──────────────────
// Before W11 these were unconditional: a body conflict was caught while its meta
// was silently overwritten. Now each write is If-Match the version this device last
// wrote or merged (db.remoteTags), and a refusal merges rather than overwrites.

const MAX_SIBLING_WRITES = 3

async function recordTag(path: string, eTag: string | undefined): Promise<void> {
  if (eTag) await db.remoteTags.put({ path, eTag })
}

/** Work meta.json and a chapter's {id}.json: LWW by content stamp, now conditional. A refusal
 *  re-reads: a strictly newer remote is adopted here (and this write dropped), else this
 *  device's copy goes up `If-Match` the version just read. */
async function uploadLww(entry: SyncQueueEntry): Promise<WrittenFile | null> {
  const payload = entry.payload ?? ''
  const localStamp = (parseDeltaJson<{ updatedAt?: number }>(payload, entry.remotePath)?.updatedAt) ?? 0
  const known = await db.remoteTags.get(entry.remotePath)
  let pre: WritePrecondition | null = known ? { kind: 'if-match', tag: known.eTag } : null
  for (let attempt = 1; ; attempt += 1) {
    if (!pre) {
      const file = await readFileFromOneDrive(entry.remotePath)
      if (file) {
        const remoteStamp = parseDeltaJson<{ updatedAt?: number }>(file.content, entry.remotePath)?.updatedAt ?? -1
        if (remoteStamp > localStamp) {
          await adoptNewerMeta(entry, file)
          return null
        }
        pre = { kind: 'if-match', tag: file.etag }
      } else {
        pre = { kind: 'expect-absent' }
      }
    }
    try {
      const written = await writeFileToOneDrive(entry.remotePath, payload, pre)
      await recordTag(entry.remotePath, written.etag)
      // A work's own meta write: remember its tag on the row too, so the delta echo of this
      // device's own write is skipped (applyDeltaItem's eTag-match early return).
      if (entry.entityType === 'work' && written.etag) await db.works.update(entry.workId, { cTag: written.etag })
      return written
    } catch (err) {
      if (!isDocConflict(err) || attempt >= MAX_SIBLING_WRITES) throw err
      pre = null
    }
  }
}

/** A refused LWW meta write met a strictly newer remote: take it, as a down-sync would. */
async function adoptNewerMeta(entry: SyncQueueEntry, file: RemoteFile): Promise<void> {
  if (entry.entityType === 'work') {
    await adoptWorkMeta(entry.workId, file)
  } else if (isChapterMetaEntry(entry)) {
    await adoptChapterMeta(entry.workId, entry.entityId.slice(0, -':meta'.length), file)
  }
}

type AggregateRow = { id: string; updatedAt: number; workId: string }

/** The aggregate tables, by queue entity type. */
function aggregateFor(entityType: SyncQueueEntry['entityType']): { table: Table<AggregateRow, string>; type: AggregateEntityType } | null {
  const t = (table: unknown) => table as Table<AggregateRow, string>
  switch (entityType) {
    case 'character': return { table: t(db.characters), type: 'character' }
    case 'pairing':   return { table: t(db.pairings), type: 'pairing' }
    case 'plotpoint': return { table: t(db.plotPoints), type: 'plotpoint' }
    case 'canonnote': return { table: t(db.canonNotes), type: 'canonnote' }
    case 'scene':     return { table: t(db.scenes), type: 'scene' }
    case 'cut':       return { table: t(db.cuts), type: 'cut' }
    default:          return null
  }
}

/**
 * An aggregate file: `writeRebasingItem` with `mergeAggregateFiles` as the rebase (§4 quil row).
 * A refusal re-reads the other device's file, merges it per entity (newest stamp wins, tombstones
 * included) with this device's, and writes the union `If-Match` the version just read, at most
 * three times. The landed union is then merged into Dexie, so this device holds the other's
 * additions too. A path with no known tag is read first.
 */
async function uploadAggregate(entry: SyncQueueEntry): Promise<{ recreated: boolean }> {
  const payload = entry.payload ?? ''
  const known = await db.remoteTags.get(entry.remotePath)
  let landed = payload
  let lastTag: string | undefined
  const result = await writeRebasingItem<string>(
    {
      read: async () => {
        const f = await readFileFromOneDrive(entry.remotePath)
        return { value: f?.content ?? null, tag: f?.etag || null }
      },
      write: async (value, pre) => {
        const w = await writeFileToOneDrive(entry.remotePath, value, pre)
        landed = value
        lastTag = w.etag
        return { tag: w.etag || null, ...(w.recreated ? { recreated: true as const } : {}) }
      },
    },
    (fresh) => {
      if (fresh === null) return payload
      const mine = parseAggregate<{ id: string; updatedAt: number }>(payload, entry.remotePath)
      const theirs = parseAggregate<{ id: string; updatedAt: number }>(fresh, entry.remotePath)
      if (!mine || !theirs) return payload // a corrupt remote is replaced by this device's copy (D9)
      return JSON.stringify(mergeAggregateFiles(mine, theirs))
    },
    MAX_SIBLING_WRITES,
    known ? { value: payload, pre: { kind: 'if-match', tag: known.eTag } } : undefined,
  )
  await recordTag(entry.remotePath, lastTag)
  const agg = aggregateFor(entry.entityType)
  if (agg && landed !== payload) {
    const parsed = parseAggregate<AggregateRow>(landed, entry.remotePath)
    if (parsed) await mergeAggregate(agg.table, agg.type, entry.workId, parsed)
  }
  return { recreated: result.recreated }
}

// ─── Upload (local → OneDrive) ────────────────────────────────────────────────

/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export async function processUploadQueue(onItem?: ProgressItemFn): Promise<void> {
  const now = Date.now()
  const pending = await db.syncQueue
    .where('nextRetryAt')
    .belowOrEqual(now)
    .toArray()

  if (pending.length === 0) { onItem?.(0, 0); return }

  // Drive the top-bar "Saving" pill + record one write outcome for the drain.
  setSyncActive(true)
  const recreatedSiblings: SyncQueueEntry[] = []
  let successes = 0
  let hadError = false
  let lastError: string | undefined
  try {
    for (const [i, entry] of pending.entries()) {
      // Report how many entries are settled *before* this one — fires on every path
      // (success, conflict-drop, retry) since it's the first thing each iteration does.
      onItem?.(i, pending.length)
      // An earlier entry in this drain may have cancelled this one (a refused chapter delete
      // restores the chapter and withdraws its meta delete): only what is still queued runs.
      if (entry.id != null && !(await db.syncQueue.get(entry.id))) continue
      try {
        if (entry.operation === 'upload' && entry.payload != null) {
          // The work may have been deleted after this upload was enqueued — an
          // in-flight autosave landing just after Dashboard's delete, or the
          // crash-flush sweep re-driving a chapter for a since-deleted work.
          // There's nothing left to upload it for, and retrying only hammers
          // Graph with folder creates against a delete that may still be
          // settling (seen as a permanent "Failed to create folder … 400"
          // loop). Drop it, same as a resolved conflict.
          if (entry.id != null && !(await db.works.get(entry.workId))) {
            await db.syncQueue.delete(entry.id)
            continue
          }
          await ensureWorkFolders(entry.workId)
          if (isProseEntry(entry)) {
            // Decide later keeps a conflicted chapter's uploads paused (W11 §3.5): its body
            // waits on this device until the writer chooses, and the choice writes it.
            if (await (await conflictPaths()).hasOpenProseConflict(entry.entityId)) {
              if (entry.id != null) await db.syncQueue.delete(entry.id)
              continue
            }
            await uploadProse(entry)
          } else if (aggregateFor(entry.entityType)) {
            const { recreated } = await uploadAggregate(entry)
            if (recreated) recreatedSiblings.push(entry)
          } else if (entry.entityType === 'work' || isChapterMetaEntry(entry)) {
            const written = await uploadLww(entry)
            if (written?.recreated) recreatedSiblings.push(entry)
          } else {
            await writeFileToOneDrive(entry.remotePath, entry.payload)
          }

        } else if (entry.operation === 'delete') {
          if (entry.entityId.startsWith('delete:chapter:prose:')) {
            // Conditional (W11 decision 2): a refusal restores the other device's edit.
            await (await conflictPaths()).pushChapterDelete(entry)
          } else if (entry.entityId.startsWith('delete:work:meta:')) {
            await (await conflictPaths()).pushWorkDelete(entry)
          } else {
            // A chapter's meta beside its (already conditional) body, and a work's folder after
            // its (already conditional) meta.json: these follow their unit's decision.
            await deleteFileFromOneDrive(entry.remotePath, entry.ifMatch)
          }
        }

        // Success — remove from queue
        if (entry.id != null) await db.syncQueue.delete(entry.id)
        successes += 1

      } catch (err) {
        if (entry.id == null) continue

        // A prose conflict: both versions are kept as a ConflictRecord. Drop the queue
        // entry so we stop retrying and wait for the writer to resolve it. (A meta or
        // aggregate refusal that outlived its rebases is retried like any failure below:
        // dropping it would lose the edit.)
        if (isDocConflict(err) && isProseEntry(entry)) {
          await db.syncQueue.delete(entry.id)
          continue
        }

        // Network down: pause this drain and retry later WITHOUT counting toward
        // the parked state — offline is a normal condition for this app, not the
        // entry's failure. The 'online' listener un-parks + resyncs on reconnect.
        // Not a write failure for status purposes — the pill already says Offline.
        if (isNetworkError(err)) {
          await db.syncQueue.update(entry.id, { nextRetryAt: Date.now() + OFFLINE_RETRY_DELAY_MS, errorMessage: 'offline: waiting for connection' })
          break
        }

        // Auth needs re-interaction: pause this drain (don't spawn more blocked
        // popups) and retry later WITHOUT counting toward the parked state, so sync
        // self-heals the moment the user re-authenticates rather than wedging (B4).
        // Not a write failure for status purposes — it's a reconnect situation.
        if (isAuthInteractionError(err)) {
          await db.syncQueue.update(entry.id, { nextRetryAt: Date.now() + AUTH_RETRY_DELAY_MS, errorMessage: 'auth: reconnect needed' })
          break
        }

        hadError = true
        lastError = String(err)
        const newCount = entry.retryCount + 1
        const update =
          newCount > MAX_ENTRY_RETRIES
            ? { retryCount: newCount, nextRetryAt: Number.MAX_SAFE_INTEGER, errorMessage: String(err) }
            : { retryCount: newCount, nextRetryAt: Date.now() + Math.min(Math.pow(2, newCount), MAX_RETRY_DELAY_S) * 1000, errorMessage: String(err) }

        await db.syncQueue.update(entry.id, update)
      }
    }
  } finally {
    setSyncActive(false)
    onItem?.(pending.length, pending.length)
  }

  // Meta and aggregate writes that recreated a vanished file: decide at the unit the writer
  // knows (the work, the chapter), once the drain is done (W11 §3.4 rules 2 and 3).
  if (recreatedSiblings.length > 0) {
    const paths = await conflictPaths()
    for (const entry of recreatedSiblings) {
      try { await paths.onSiblingRecreated(entry) } catch (err) { console.warn('[Quil sync] recreated', err) }
    }
  }

  if (hadError) logSync('write', false, lastError)
  else if (successes > 0) {
    logSync('write', true)
    noteErrorSyncSuccess()
  }
  // The drain has settled either way, so the once-a-day expectation checks can judge it
  // (the stale-autosave check reads the queue this drain just worked through).
  runQuilChecks()
}

// ─── Download (OneDrive → local) ──────────────────────────────────────────────

type ParsedPath =
  | { type: 'work'; workId: string }
  | { type: 'chapter-prose'; workId: string; chapterId: string }
  | { type: 'chapter-meta'; workId: string; chapterId: string }
  | { type: 'characters'; workId: string }
  | { type: 'pairings'; workId: string }
  | { type: 'plotpoints'; workId: string }
  | { type: 'canonnotes'; workId: string }
  | { type: 'scenes'; workId: string }
  | { type: 'cuts'; workId: string }

function parseDeltaPath(parentReferencePath: string, name: string): ParsedPath | null {
  // parentReferencePath: "/drive/root:/Apps/Quil/works/{workId}/chapters" etc.
  // Derive the marker from the configured root so it tracks QUIL_ONEDRIVE_ROOT.
  const marker = `${QUIL_ONEDRIVE_ROOT}/works/`
  const idx = parentReferencePath.indexOf(marker)
  if (idx === -1) return null

  const after = parentReferencePath.slice(idx + marker.length)
  const parts = after.split('/')
  const workId = parts[0]
  if (!workId) return null

  if (parts.length === 1) {
    if (name === 'meta.json') return { type: 'work', workId }
    if (name === 'characters.json') return { type: 'characters', workId }
    if (name === 'pairings.json') return { type: 'pairings', workId }
    if (name === 'plotpoints.json') return { type: 'plotpoints', workId }
    if (name === 'canonnotes.json') return { type: 'canonnotes', workId }
    if (name === 'scenes.json') return { type: 'scenes', workId }
    if (name === 'cuts.json') return { type: 'cuts', workId }
  }

  if (parts.length === 2 && parts[1] === 'chapters') {
    // Order matters: {id}.prose.json also ends with .json (meta is bare {id}.json).
    if (name.endsWith('.prose.json')) {
      return { type: 'chapter-prose', workId, chapterId: name.slice(0, -'.prose.json'.length) }
    }
    if (name.endsWith('.json')) {
      return { type: 'chapter-meta', workId, chapterId: name.slice(0, -'.json'.length) }
    }
  }

  // In-scope path we couldn't classify (a rename / unexpected nesting). Surface it
  // so silent sync drift is observable rather than invisibly dropped (B11).
  if (name !== 'chapters') console.warn('[Quil sync] unparseable delta item under /Quil/works/', after, name)
  return null
}

// ─── Aggregate merge (characters / pairings / plotpoints / canon notes) ─────────
// Aggregate files carry { entities, tombstones }. Merging per-entity (rather than
// replacing the whole local set) preserves un-uploaded local additions, while
// tombstones propagate deletions. Per id, the newest timestamp wins between the
// entity's updatedAt and any tombstone's deletedAt. Legacy bare-array files
// (no tombstones) are read as { entities, tombstones: [] }.

interface AggregateFile<T> {
  entities: T[]
  tombstones: { id: string; deletedAt: number }[]
}

/**
 * Poison-file isolation (D9). A single corrupt JSON file in the delta (truncated
 * upload, disk glitch, a hand-edit in OneDrive) must not throw out of the down-sync
 * loop — if it did, the batch would fail before the delta token advanced and the SAME
 * bad file would be re-fetched forever, freezing ALL future down-sync. So a JSON parse
 * failure is treated as a skippable per-item error: log the path, return null, let the
 * loop move on and the token advance. Transient FETCH failures are different — they
 * happen earlier in readFileFromOneDrive and still throw, so a network blip retries the
 * whole batch (with the token unchanged) rather than skipping real data.
 */
function parseDeltaJson<T>(content: string, pathForLog: string): T | null {
  try {
    return JSON.parse(content) as T
  } catch (err) {
    console.warn(`[Quil sync] skipping corrupt delta file: ${pathForLog}`, err)
    logSync('pull', false, `corrupt file skipped: ${pathForLog}`)
    return null
  }
}

function parseAggregate<T>(content: string, pathForLog: string): AggregateFile<T> | null {
  const parsed = parseDeltaJson<unknown>(content, pathForLog)
  if (parsed == null) return null
  if (Array.isArray(parsed)) return { entities: parsed as T[], tombstones: [] }
  const obj = parsed as { entities?: T[]; tombstones?: { id: string; deletedAt: number }[] }
  return { entities: obj.entities ?? [], tombstones: obj.tombstones ?? [] }
}

async function mergeAggregate<T extends { id: string; updatedAt: number; workId: string }>(
  table: Table<T, string>,
  entityType: AggregateEntityType,
  workId: string,
  file: AggregateFile<T>,
): Promise<void> {
  await db.transaction('rw', table, db.tombstones, async () => {
    const locals = await table.where('workId').equals(workId).toArray()
    const localById = new Map<string, T>(locals.map(e => [e.id, e]))
    const localTombs = await db.tombstones.where('[workId+entityType]').equals([workId, entityType]).toArray()
    const tombAt = new Map<string, number>(localTombs.map(t => [t.entityId, t.deletedAt]))

    // Latest known timestamp for an id, across its live entity and its tombstone.
    const stampOf = (id: string) => Math.max(localById.get(id)?.updatedAt ?? -1, tombAt.get(id) ?? -1)

    // Remote entities win only when strictly newer than what we hold for that id.
    for (const r of file.entities) {
      if (r.updatedAt > stampOf(r.id)) {
        await table.put(withoutRetired(table.name, r))
        localById.set(r.id, r)
        if (tombAt.has(r.id)) { await db.tombstones.delete(r.id); tombAt.delete(r.id) }
      }
    }
    // Remote tombstones delete locally only when newer than what we hold.
    for (const t of file.tombstones) {
      if (t.deletedAt > stampOf(t.id)) {
        if (localById.has(t.id)) { await table.delete(t.id); localById.delete(t.id) }
        await db.tombstones.put({ entityId: t.id, entityType, workId, deletedAt: t.deletedAt } as Tombstone)
        tombAt.set(t.id, t.deletedAt)
      }
    }
  })
}

/**
 * The same per-entity rule as `mergeAggregate`, as a pure merge of two FILES: the rebase an
 * aggregate upload runs when another device wrote first (W11, ledger #15). Per id, the newest
 * stamp wins across both files' entities and tombstones; a tie keeps `mine`'s state, as
 * `mergeAggregate` keeps local on a tie. Converges when re-applied (a union), which is what
 * `writeRebasingItem` requires of a rebase.
 */
function mergeAggregateFiles<T extends { id: string; updatedAt: number }>(
  mine: AggregateFile<T>,
  theirs: AggregateFile<T>,
): AggregateFile<T> {
  type Best = { stamp: number; entity?: T; tomb?: number }
  const best = new Map<string, Best>()
  const offer = (id: string, stamp: number, entity: T | undefined, tomb: number | undefined, mineSide: boolean) => {
    const cur = best.get(id)
    if (!cur || stamp > cur.stamp || (stamp === cur.stamp && mineSide)) {
      best.set(id, entity ? { stamp, entity } : { stamp, tomb })
    }
  }
  // `theirs` first, so `mine` wins every tie.
  for (const e of theirs.entities) offer(e.id, e.updatedAt, e, undefined, false)
  for (const t of theirs.tombstones) offer(t.id, t.deletedAt, undefined, t.deletedAt, false)
  for (const e of mine.entities) offer(e.id, e.updatedAt, e, undefined, true)
  for (const t of mine.tombstones) offer(t.id, t.deletedAt, undefined, t.deletedAt, true)
  const entities: T[] = []
  const tombstones: { id: string; deletedAt: number }[] = []
  for (const [id, b] of best) {
    if (b.entity) entities.push(b.entity)
    else if (b.tomb !== undefined) tombstones.push({ id, deletedAt: b.tomb })
  }
  return { entities, tombstones }
}

async function downSyncAggregate<T extends { id: string; updatedAt: number; workId: string }>(
  table: Table<T, string>,
  entityType: AggregateEntityType,
  workId: string,
  filename: string,
): Promise<void> {
  const path = `${QUIL_ONEDRIVE_ROOT}/works/${workId}/${filename}`
  const file = await readFileFromOneDrive(path)
  if (!file) return
  const parsed = parseAggregate<T>(file.content, path)
  if (!parsed) return  // corrupt aggregate — skipped + logged (D9), token still advances
  await mergeAggregate(table, entityType, workId, parsed)
  // Merged, so this version is one this device has seen: the next upload is If-Match it (W11).
  await recordTag(path, file.etag)
}

/** Adopt a work's meta.json as read (strictly newer by content stamp, or new to this device). */
async function adoptWorkMeta(workId: string, file: RemoteFile): Promise<void> {
  const workPath = `${QUIL_ONEDRIVE_ROOT}/works/${workId}/meta.json`
  const work = parseDeltaJson<Work>(file.content, workPath)
  if (!work) return
  await db.works.put({ ...withoutRetired('works', work), cTag: file.etag })
  await recordTag(workPath, file.etag)
}

/** Adopt a chapter's meta as read, keeping this device's prose and device-local flags. */
async function adoptChapterMeta(workId: string, chapterId: string, file: RemoteFile): Promise<void> {
  const metaPath = `${QUIL_ONEDRIVE_ROOT}/works/${workId}/chapters/${chapterId}.json`
  const parsed = parseDeltaJson<Omit<Chapter, 'prose'>>(file.content, metaPath)
  if (!parsed) return
  const meta = withoutRetired('chapters', parsed)
  // `proseLoaded`, `needsUpload`, `cTag` and `proseCTag` are device-local: never let a cloud
  // meta dictate them. (cTag is this device's If-Match token; legacy meta files
  // written before audit 3.1 still carry the sender's stale one.)
  delete (meta as Partial<Chapter>).proseLoaded
  delete (meta as Partial<Chapter>).needsUpload
  delete (meta as Partial<Chapter>).cTag
  delete (meta as Partial<Chapter>).proseCTag
  const local = await db.chapters.get(chapterId)
  if (local) {
    await db.chapters.update(chapterId, { ...meta, prose: local.prose, proseLoaded: local.proseLoaded, needsUpload: local.needsUpload })
  } else {
    await db.chapters.put({ ...meta, prose: '', proseLoaded: false })
  }
  await recordTag(metaPath, file.etag)
}

async function applyDeltaItem(parsed: ParsedPath, item: DeltaItem): Promise<void> {
  switch (parsed.type) {
    case 'work': {
      const local = await db.works.get(parsed.workId)
      // We already hold this exact remote version (eTag match) — skip regardless of
      // clock. This makes the common "already synced" case immune to clock skew (B2).
      if (local?.cTag && item.etag && local.cTag === item.etag) return

      const workPath = `${QUIL_ONEDRIVE_ROOT}/works/${parsed.workId}/meta.json`
      const file = await readFileFromOneDrive(workPath)
      if (!file) return

      const work = parseDeltaJson<Work>(file.content, workPath)
      if (!work) return  // corrupt work meta — skipped + logged (D9), token still advances
      // Compare CONTENT stamps, not the OneDrive file mtime (D3): an older edit that
      // was uploaded later carries a newer mtime but an OLDER updatedAt — adopting it
      // by mtime would regress a newer local edit. When local is strictly newer, heal
      // the cloud by re-pushing the local meta so the two converge.
      if (local && work.updatedAt <= local.updatedAt) {
        if (work.updatedAt < local.updatedAt) {
          // Read and judged older: this version is seen, so the heal is If-Match it (W11).
          await recordTag(workPath, file.etag)
          await reEnqueueWorkMeta(local)
        }
        return
      }
      await adoptWorkMeta(parsed.workId, file)
      break
    }

    case 'chapter-prose': {
      // LAZY PROSE: never download story bodies during sync — that's what made cold
      // start crawl. A new chapter is created by its meta item (proseLoaded:false); the
      // Writing Surface fetches the body on open (ensureChapterProse). Here we only
      // INVALIDATE a stale local copy so the next open re-fetches.
      const local = await db.chapters.get(parsed.chapterId)
      if (!local) return // meta item will create it (proseLoaded:false)
      if (local.cTag && item.etag && local.cTag === item.etag) return // already current
      if (local.proseLoaded === false) return // already invalidated / never loaded

      // A pending local upload means our copy is the newer one — don't invalidate it.
      // A crash-flush dirty marker (needsUpload, D2) counts the same: the body was
      // written locally but not yet enqueued, so treat it as pending. The launch
      // sweep enqueues it and the resulting upload 412s → records a conflict, rather
      // than this down-sync silently discarding the un-uploaded local prose.
      const pendingCount = await db.syncQueue.where('entityId').equals(parsed.chapterId).count()
      if (pendingCount > 0 || local.needsUpload) return

      await db.chapters.update(parsed.chapterId, { proseLoaded: false })
      break
    }

    case 'chapter-meta': {
      const local = await db.chapters.get(parsed.chapterId)

      // Don't clobber an in-flight local metadata edit (mirrors the prose guard — B5).
      if (local) {
        const pendingMeta = await db.syncQueue.where('entityId').equals(`${parsed.chapterId}:meta`).count()
        if (pendingMeta > 0) return
      }

      const metaPath = `${QUIL_ONEDRIVE_ROOT}/works/${parsed.workId}/chapters/${parsed.chapterId}.json`
      const file = await readFileFromOneDrive(metaPath)
      if (!file) return

      const meta = parseDeltaJson<Omit<Chapter, 'prose'>>(file.content, metaPath)
      if (!meta) return  // corrupt chapter meta — skipped + logged (D9), token still advances
      // Compare CONTENT stamps, not the OneDrive file mtime (D3): an older edit
      // uploaded later has a newer mtime but an older updatedAt — adopting it by mtime
      // would regress a newer local edit. Heal the cloud when local is strictly newer.
      if (local && meta.updatedAt <= local.updatedAt) {
        if (meta.updatedAt < local.updatedAt) {
          await recordTag(metaPath, file.etag)
          await reEnqueueChapterMeta(local)
        }
        return
      }
      // Merge: keep local prose + this device's hydration/dirty flags, apply cloud
      // metadata (incl. the up-to-date wordCount the Dashboard reads). A new chapter from
      // another device keeps its prose in OneDrive until this device opens it (lazy).
      await adoptChapterMeta(parsed.workId, parsed.chapterId, file)
      break
    }

    case 'characters':
      await downSyncAggregate(db.characters, 'character', parsed.workId, 'characters.json')
      break

    case 'pairings':
      await downSyncAggregate(db.pairings, 'pairing', parsed.workId, 'pairings.json')
      break

    case 'plotpoints':
      await downSyncAggregate(db.plotPoints, 'plotpoint', parsed.workId, 'plotpoints.json')
      break

    case 'canonnotes':
      await downSyncAggregate(db.canonNotes, 'canonnote', parsed.workId, 'canonnotes.json')
      break

    case 'scenes':
      await downSyncAggregate(db.scenes, 'scene', parsed.workId, 'scenes.json')
      break

    case 'cuts':
      await downSyncAggregate(db.cuts, 'cut', parsed.workId, 'cuts.json')
      break
  }
}

/**
 * A file was deleted on another device (delta `deleted` flag). Mirror it locally.
 * Guarded by pending uploads — unsynced local edits re-create the item rather
 * than being dropped. Aggregate-entity deletions are NOT handled here; they ride
 * inside the aggregate file's tombstones (see mergeAggregate). deleteWork deletes
 * meta.json explicitly so a whole-work deletion surfaces as a parseable item.
 */
async function applyDeltaDeletion(parsed: ParsedPath): Promise<void> {
  switch (parsed.type) {
    case 'chapter-prose':
    case 'chapter-meta': {
      const pending = await db.syncQueue.where('entityId').equals(parsed.chapterId).count()
      const pendingMeta = await db.syncQueue.where('entityId').equals(`${parsed.chapterId}:meta`).count()
      // Unsynced local edits bring it back (W11 decision 1, edit wins): their If-Match upload
      // lands as a recreate, and that path tells the writer and offers Delete again.
      if (pending + pendingMeta > 0) return
      if (!(await db.chapters.get(parsed.chapterId))) return // the prose and meta items both come
      // Clean here: the other device's delete wins, but no longer silently (ledger #9): a copy
      // goes to Recently deleted first, and a notice says so when it held writing.
      await (await conflictPaths()).followRemoteChapterDelete(parsed.chapterId)
      break
    }
    case 'work': {
      // meta.json deleted → the whole work was deleted elsewhere. Cascade locally, keeping a copy in
      // Recently deleted first (syncConflicts.followRemoteWorkDelete, W12 Phase B).
      const pending = await db.syncQueue.where('workId').equals(parsed.workId).count()
      if (pending > 0) return
      await (await conflictPaths()).followRemoteWorkDelete(parsed.workId)
      break
    }
    default:
      break // aggregate-file deletions etc. — ignore (handled via content/tombstones)
  }
}

// How many OneDrive reads to run at once during a down-sync. Delta items are
// independent (prose is no longer fetched here), so a bounded pool turns O(n)
// sequential round-trips into O(n / pool) — the second half of the cold-start win.
const DOWN_SYNC_CONCURRENCY = 8

/** Run an async op over items with a fixed concurrency cap. */
async function mapWithConcurrency<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  // One iterator shared by every worker: each next() hands a distinct item to whichever
  // worker asks first, and the pool drains when it is exhausted.
  const queue = items.values()
  const worker = async () => {
    for (const item of queue) await fn(item)
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
}

/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export async function runDownSync(onItem?: ProgressItemFn): Promise<void> {
  const settings = await ensureSettings()
  const { items, nextToken } = await runDeltaQuery(settings.deltaToken)

  let done = 0
  await mapWithConcurrency(items, DOWN_SYNC_CONCURRENCY, async (item) => {
    const parsed = parseDeltaPath(item.parentReferencePath, item.name)
    if (parsed) {
      if (item.deleted) {
        await applyDeltaDeletion(parsed)
      } else {
        await applyDeltaItem(parsed, item)
      }
    }
    // Count every delta item (including in-scope skips) so the bar reaches total.
    onItem?.(++done, items.length)
  })

  if (nextToken) {
    await db.settings.update(1, { deltaToken: nextToken })
  }
}

// ─── OneDrive-first cold-start (Phase 2) ────────────────────────────────────────

/**
 * The one-time cold-start sync run behind the retrieving-the-library barrier on
 * the first gated sign-in for a device. Ordered **down-then-up**: pull + merge the
 * cloud into local FIRST (so a not-yet-synced device isn't shown a false-empty
 * library it would duplicate into), THEN push any local-only data up via
 * reconcileLocalToCloud (safe now that local holds the cloud union — the whole-file
 * aggregate uploads can't drop cloud-only entities), drain, and mark the device done.
 * On a fresh device the reconcile is a no-op; on the existing local-first device it
 * migrates all pre-gate local work up to OneDrive without orphaning anything.
 */
export async function runInitialSync(onProgress?: InitialSyncProgressFn): Promise<void> {
  // The cold start IS a sync in flight: hold the shared mutex so the background
  // listeners (armed by AuthProvider as soon as the account exists, ~3s trigger)
  // can't run a second processUploadQueue over the same queue snapshot — a
  // concurrent double prose upload 412s against its own etag advance and records
  // a conflict between two identical versions (audit 2.7).
  // Exclusive (waiting) lock — unlike the background triggers this must always run
  // the cold-start barrier, so if another tab is mid-sync we wait for it, then run (D5).
  await runWaiting(async () => {
    try {
      await runInitialSyncInner(onProgress)
      logSync('load', true)
      noteErrorSyncSuccess()
    } catch (err) {
      logSync('load', false, String(err))
      throw err
    }
  })
}

async function runInitialSyncInner(onProgress?: InitialSyncProgressFn): Promise<void> {
  onProgress?.({ phase: 'connecting' })
  await ensureQuilFolders()
  // Pull + merge the cloud down. The delta query returns a known item count, so the
  // gate can show a genuine determinate bar as each file is applied.
  onProgress?.({ phase: 'pulling', itemsDone: 0, itemsTotal: 0 })
  await runDownSync((done, total) =>
    onProgress?.({ phase: 'pulling', itemsDone: done, itemsTotal: total }))
  // Story count is genuinely known once the cloud is down. Surface it from here on.
  const stories = await db.works.count()
  onProgress?.({ phase: 'merging', stories })
  // Belt-and-braces before the reconcile push: the delta query is eventually
  // consistent and may omit a recently-changed aggregate, so re-read + merge each
  // local work's aggregate files directly. This guarantees local holds the cloud
  // union, so reconcileLocalToCloud's whole-file aggregate push can't drop a
  // cloud-only entity (B1).
  await mergeLocalWorkAggregates()
  onProgress?.({ phase: 'saving', itemsDone: 0, itemsTotal: 0, stories })
  await reconcileLocalToCloud()
  await processUploadQueue((done, total) =>
    onProgress?.({ phase: 'saving', itemsDone: done, itemsTotal: total, stories }))
  await db.settings.update(1, { initialSyncComplete: true })
  onProgress?.({ phase: 'done', stories: await db.works.count() })
}

/** Re-read + merge every local work's aggregate files from OneDrive (see B1). */
async function mergeLocalWorkAggregates(): Promise<void> {
  await mergeWorkAggregates((await db.works.toArray()).map(w => w.id))
}

/** Re-read + merge these works' aggregate files from OneDrive. Also used by a refused work
 *  delete (W11), which restores a work and must take the other device's planning back in. */
export async function mergeWorkAggregates(workIds: string[]): Promise<void> {
  const tasks: Array<() => Promise<void>> = []
  for (const id of workIds) {
    tasks.push(() => downSyncAggregate(db.characters, 'character', id, 'characters.json'))
    tasks.push(() => downSyncAggregate(db.pairings, 'pairing', id, 'pairings.json'))
    tasks.push(() => downSyncAggregate(db.plotPoints, 'plotpoint', id, 'plotpoints.json'))
    tasks.push(() => downSyncAggregate(db.canonNotes, 'canonnote', id, 'canonnotes.json'))
    tasks.push(() => downSyncAggregate(db.scenes, 'scene', id, 'scenes.json'))
    tasks.push(() => downSyncAggregate(db.cuts, 'cut', id, 'cuts.json'))
  }
  await mapWithConcurrency(tasks, DOWN_SYNC_CONCURRENCY, t => t())
}

/**
 * Drop aggregate tombstones past the retention window (W11 decision 7: 90 days). The upload
 * payload already leaves them out (storage.enqueueAggregateSync), so the next aggregate write
 * carries the collection to OneDrive. Returns how many went.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export async function pruneTombstones(now: number = serverNow()): Promise<number> {
  const cutoff = now - TOMBSTONE_RETENTION_MS
  const stale = (await db.tombstones.toArray()).filter(t => t.deletedAt < cutoff).map(t => t.entityId)
  if (stale.length > 0) await db.tombstones.bulkDelete(stale)
  return stale.length
}

// ─── Combined sync trigger ────────────────────────────────────────────────────

/**
 * Cross-tab sync mutex (D5, B10), now the canonical W11 lock (lib/syncLock.ts). Two tabs of
 * the same app draining the shared syncQueue concurrently double-upload prose and fabricate
 * a 412 (the second upload's If-Match is stale against the first's etag advance). Web Locks
 * first, exactly as before. Where `navigator.locks` is missing (old Safari, an embedded
 * webview, jsdom) the old code ran UNLOCKED, guarded only by this tab's own flag; the shared
 * lock falls back to a localStorage lease instead, so two such tabs still exclude each other.
 * Background triggers 'skip' when another holder has it (its sync covers the shared
 * OneDrive); the cold-start barrier and a conflict resolution 'wait'. Named for the local
 * database the lock protects (every tab shares it). `maxHoldMs` 300 s: a first sign-in can
 * reconcile a whole library under one hold; past it the holder's signal aborts and the lock
 * is released, so a hung request can never starve the other tabs.
 */
// W12 A5: the lock is named for the STORE (`quil:<store>:sync`), so it is resolved at call time, after
// storeBinding has pointed syncCoordination at the bound store. The adopted legacy store keeps the
// name it always had.

/** Background work: run under the lock, or skip at once when another holder has it. */
function runSkipping(fn: () => Promise<void>): Promise<unknown> {
  return appSyncLock().run(() => fn(), 'skip')
}

/**
 * Run under the lock in 'wait' mode. The shared lock refuses a 'wait' while THIS tab holds it
 * (it cannot tell a re-entrant call from a concurrent one, and waiting on itself would
 * deadlock), so a caller that arrives while a background drain in this tab is running waits
 * for that drain to release, then queues. The wait is for the drain, never for a prompt: the
 * lock never covers one (W11 §3.1).
 */
async function runWaiting<T>(fn: () => Promise<T>): Promise<T> {
  for (;;) {
    const lock = appSyncLock()
    if (lock.heldHere()) await new Promise<void>(resolve => lock.afterRelease(resolve))
    try {
      const result = await lock.run(() => fn(), 'wait')
      if (result.ran) return result.value
      throw new Error('quil sync: the sync lock was not granted in wait mode')
    } catch (err) {
      // Another drain in this tab took the lock between the release and this call: go round.
      if (err instanceof Error && /would wait on itself/.test(err.message) && lock.heldHere()) continue
      throw err
    }
  }
}

async function syncBody(): Promise<void> {
  try {
    await ensureQuilFolders()
    // Log the pull outcome so the monitor can flag persistently-stale reads even
    // while writes are fine. Uploads log their own 'write' outcome.
    try {
      await runDownSync()
      logSync('pull', true)
      noteErrorSyncSuccess()
    } catch (err) {
      console.warn('[Quil sync] pull', err)
      logSync('pull', false, String(err))
    }
    await pruneTombstones()
    await processUploadQueue()
  } catch (err) {
    console.warn('[Quil sync]', err)
  }
}

export async function triggerSync(): Promise<void> {
  await runSkipping(syncBody)
}

/**
 * A sync the caller can rely on having run: where triggerSync returns at once when another tab (or a
 * drain in this one) holds the lock, this waits for that holder to finish, then syncs. Sign-out uses it
 * so its recount does not warn about changes the other tab is about to land. The wait is capped (the
 * holder may be stuck); on the cap it resolves and the caller's recount says what is left.
 */
export async function triggerSyncWaiting(timeoutMs = 15_000): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const cap = new Promise<void>(resolve => { timer = setTimeout(resolve, timeoutMs) })
  try {
    await Promise.race([runWaiting(syncBody), cap])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Manual retry from the top-bar pill ("Sync failed" / "Not syncing"). Resets
 * every queued entry to due-now — including entries parked at MAX_SAFE_INTEGER
 * after exhausting retries — then runs a full sync (re-pull + drain).
 */
export async function retrySync(): Promise<void> {
  const ids = await db.syncQueue.toCollection().primaryKeys()
  for (const id of ids) await db.syncQueue.update(id, { nextRetryAt: 0, retryCount: 0 })
  await triggerSync()
}

/**
 * Drain just the upload queue (local → cloud) without a full down-sync, so
 * OneDrive stays a near-live backup during a focused writing session. A cheap
 * no-op when nothing is due. Shares the sync lock with triggerSync so a drain
 * and a full sync never overlap, in this tab or another. Down-sync stays on the
 * visibility/online/post-auth path — a single user rarely needs to pull
 * mid-session, and this keeps delta-query volume sane.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export async function drainUploads(): Promise<void> {
  const dueCount = await db.syncQueue.where('nextRetryAt').belowOrEqual(Date.now()).count()
  if (dueCount === 0) return
  await runSkipping(async () => {
    try {
      await ensureQuilFolders()
      await processUploadQueue()
    } catch (err) {
      console.warn('[Quil sync] upload drain', err)
    }
  })
}

/**
 * Resolve a prose conflict with the writer's choice (W11 §3.5), under the sync lock in 'wait'
 * mode, so a resolution never interleaves with a drain in any tab. The dialog is opened
 * outside the lock (a liveQuery on the record), and only the resolution takes it.
 */
export async function resolveProseConflict(
  key: string,
  choice: 'keep-local' | 'keep-remote' | 'keep-both',
  stitched?: string,
): Promise<'resolved' | 'reopened' | 'deferred' | undefined> {
  const paths = await conflictPaths()
  return runWaiting(() => paths.resolveProse(key, choice, stitched))
}

// ─── Sync state (for the save indicator) ───────────────────────────────────────

/**
 * Derive a work's sync state from persisted data, so the UI can observe it via
 * liveQuery. 'syncing' is transient (not in IndexedDB) and intentionally omitted
 * — the UI shows that from the save lifecycle instead. Precedence:
 * conflict > error (an upload parked after max retries) > pending > synced.
 */
export async function getWorkSyncState(workId: string): Promise<SyncStatus> {
  const open = await db.syncConflicts.where('workId').equals(workId).filter(r => !r.resolved).count()
  if (open > 0) return 'conflict'
  const entries = await db.syncQueue.where('workId').equals(workId).toArray()
  if (entries.some(e => e.nextRetryAt === Number.MAX_SAFE_INTEGER)) return 'error'
  if (entries.length > 0) return 'pending'
  return 'synced'
}

// ─── Event listeners ──────────────────────────────────────────────────────────

const UPLOAD_DRAIN_INTERVAL_MS = 30_000

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let drainTimer: ReturnType<typeof setTimeout> | null = null

function scheduleSyncSoon(delay = 2000): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => triggerSync(), delay)
}

function scheduleUploadDrain(delay = 2000): void {
  if (drainTimer) clearTimeout(drainTimer)
  drainTimer = setTimeout(() => drainUploads(), delay)
}

/**
 * Wire up sync triggers. Call once when the user account becomes available.
 * Returns a cleanup function for React effects.
 */
export function initSyncListeners(): () => void {
  // Error sync: the capture module (configured in main.tsx before render) gets its OneDrive
  // transport here. The shared Graph client owns the token (onedrive.connectGraph), so the
  // token argument errorLog hands the transport is ignored. The errors
  // file lives at the app root, addressed approot-relative like every other Quil path.
  configureErrorSync({
    ...errorSyncConfig(),
    transport: {
      read: (_token, name) =>
        readFileFromOneDrive(`${QUIL_ONEDRIVE_ROOT}/${name}`).then((f) => f?.content ?? null),
      write: async (_token, name, text) => {
        await writeFileToOneDrive(`${QUIL_ONEDRIVE_ROOT}/${name}`, text)
      },
    },
  })

  // Reconnect first un-parks any entries that exhausted their retries during an
  // outage, so coming back online always resumes the backup without a manual retry.
  const onOnline = () => { void unparkSyncQueue().finally(() => scheduleSyncSoon(1000)) }
  const onVisibility = () => {
    if (document.visibilityState === 'visible') scheduleSyncSoon(500)
  }
  // A save (or any write) enqueues an upload and fires this; drain it promptly.
  const onQueued = () => scheduleUploadDrain(2000)

  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener(QUEUE_UPDATED_EVENT, onQueued)

  // Periodic backstop: drain stragglers and retry-eligible entries even if the
  // tab stays focused and no new writes land.
  const drainInterval = setInterval(() => { void drainUploads() }, UPLOAD_DRAIN_INTERVAL_MS)

  // Recover any crash-flushed prose that never got enqueued (D2): sweep the dirty
  // markers into real upload entries at launch, then the scheduled sync ships them.
  void sweepCrashFlushedProse().catch(err => console.warn('[Quil sync] crash-flush sweep', err))

  // Kick off an initial sync shortly after auth
  scheduleSyncSoon(3000)

  return () => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener(QUEUE_UPDATED_EVENT, onQueued)
    clearInterval(drainInterval)
    if (debounceTimer) clearTimeout(debounceTimer)
    if (drainTimer) clearTimeout(drainTimer)
  }
}
